
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, ArrowLeft, Users } from "lucide-react";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import CustomerNotes from "./CustomerNotes";
import CustomerProjects from "./CustomerProjects";
import CustomerEstimates from "./CustomerEstimates";
import CustomerInvoices from "./CustomerInvoices";
import CustomerCredits from "./CustomerCredits";
import CustomerEmailHistory from "./CustomerEmailHistory";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Customer } from "@/lib/types";
import ResponsiveTabsList from "@/components/shared/ResponsiveTabsList";

type ReferredCustomer = {
  id: string;
  name: string;
  last_name: string;
}

interface CustomerDetailsProps {
  selectedCustomer: Customer | null;
  setViewMode: (mode: 'list' | 'detail') => void;
  setEditing: (customer: Customer | null) => void;
  setOpen: (open: boolean) => void;
  handleDeleteCustomer: (customerId: string) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  navigateToCustomer: (customerId: string) => void;
}

export const CustomerDetails: React.FC<CustomerDetailsProps> = ({
  selectedCustomer,
  setViewMode,
  setEditing,
  setOpen,
  handleDeleteCustomer,
  activeTab,
  setActiveTab,
  navigateToCustomer
}) => {
  const [referringCustomer, setReferringCustomer] = useState<{ name: string, last_name: string } | null>(null);
  const [referredCustomers, setReferredCustomers] = useState<ReferredCustomer[]>([]);

  // Handle URL query param for auto-switching to emails tab
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') === 'emails') {
      setActiveTab('emails');
    }
  }, [setActiveTab]);

  // Fetch referring customer and customers referred by this customer
  useEffect(() => {
    if (!selectedCustomer) return;

    const fetchReferralData = async () => {
      // Fetch referring customer if there's a referred_by_id
      if (selectedCustomer.referred_by_id) {
        const { data: referring, error: referringError } = await supabase
          .from('customers')
          .select('name, last_name')
          .eq('id', selectedCustomer.referred_by_id)
          .single();

        if (referringError) {
          console.error('Error fetching referring customer:', referringError);
        } else {
          setReferringCustomer(referring);
        }
      }

      // Fetch customers referred by this customer
      const { data: referred, error: referredError } = await supabase
        .from('customers')
        .select('id, name, last_name')
        .eq('referred_by_id', selectedCustomer.id);

      if (referredError) {
        console.error('Error fetching referred customers:', referredError);
      } else {
        setReferredCustomers(referred || []);
      }
    };

    fetchReferralData();
  }, [selectedCustomer]);

  if (!selectedCustomer) {
    return null;
  }

  // Determine if customer is commercial
  const isCommercialCustomer = selectedCustomer.customer_type === 'commercial';

  // Get customer display name
  const getCustomerDisplayName = () => {
    if (isCommercialCustomer && selectedCustomer.business_name) {
      return selectedCustomer.business_name;
    }
    return `${selectedCustomer.name} ${selectedCustomer.last_name}`;
  };

  // Get customer phone
  const getCustomerPhone = () => {
    if (isCommercialCustomer && selectedCustomer.business_phone) {
      return selectedCustomer.business_phone;
    }
    return selectedCustomer.cell_numbers?.length ? selectedCustomer.cell_numbers.join(", ") : "None provided";
  };

  // Get customer email
  const getCustomerEmail = () => {
    if (isCommercialCustomer && selectedCustomer.business_email) {
      return selectedCustomer.business_email;
    }
    return selectedCustomer.emails?.length ? selectedCustomer.emails.join(", ") : "None provided";
  };

  return (
    <div className="mb-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <ResponsiveTabsList className="mb-4">
          <TabsTrigger value="info">Customer Info</TabsTrigger>
          <TabsTrigger value="estimates">Estimates</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="credits">Credits</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="emails">Emails</TabsTrigger>
          <TabsTrigger value="referrals">Referrals</TabsTrigger>
        </ResponsiveTabsList>
        <TabsContent value="info" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 pb-2">
              <CardTitle>Customer Information</CardTitle>
              <div className="flex gap-1">
                <Button 
                  size="icon" 
                  variant="ghost" 
                  onClick={() => { setEditing(selectedCustomer); setOpen(true); }} 
                  aria-label="Edit customer"
                  className="h-9 w-9"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      aria-label="Delete customer"
                      className="h-9 w-9"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete the customer
                        and all associated data.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => handleDeleteCustomer(selectedCustomer.id)}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    {isCommercialCustomer ? 'Business Name' : 'Name'}
                  </p>
                  <p>{getCustomerDisplayName()}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Address</p>
                  <p>
                    {(() => {
                      const addressParts = [
                        selectedCustomer.street_address,
                        selectedCustomer.suburb,
                        selectedCustomer.city,
                        selectedCustomer.province_state,
                        selectedCustomer.postal_code
                      ].filter(Boolean);
                      return addressParts.length > 0 ? addressParts.join(", ") : "None provided";
                    })()}
                  </p>
                </div>
                {isCommercialCustomer && selectedCustomer.vat_number && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">VAT Reg No.</p>
                    <p>{selectedCustomer.vat_number}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    {isCommercialCustomer ? 'Business Phone' : 'Phone Number(s)'}
                  </p>
                  <p>{getCustomerPhone()}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    {isCommercialCustomer ? 'Business Email' : 'Email(s)'}
                  </p>
                  <p>{getCustomerEmail()}</p>
                </div>
                {isCommercialCustomer && (selectedCustomer.name || selectedCustomer.representative_first_name) && (
                  <div className="col-span-1 md:col-span-2 pt-2 border-t">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Representative</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      <p><span className="text-muted-foreground">Name:</span> {selectedCustomer.representative_first_name || selectedCustomer.name} {selectedCustomer.representative_last_name || selectedCustomer.last_name}</p>
                      {(selectedCustomer.representative_phone || selectedCustomer.cell_numbers?.[0]) && (
                        <p><span className="text-muted-foreground">Phone:</span> {selectedCustomer.representative_phone || selectedCustomer.cell_numbers?.[0]}</p>
                      )}
                      {(selectedCustomer.representative_email || selectedCustomer.emails?.[0]) && (
                        <p><span className="text-muted-foreground">Email:</span> {selectedCustomer.representative_email || selectedCustomer.emails?.[0]}</p>
                      )}
                    </div>
                  </div>
                )}
                <div className="col-span-1 md:col-span-2">
                  <p className="text-sm font-medium text-muted-foreground mb-1">Lead Source</p>
                  <p>
                    {selectedCustomer.lead_source || "None specified"}
                    {selectedCustomer.lead_source === "Word of Mouth" && referringCustomer && (
                      <span className="ml-1 text-muted-foreground">
                        (Referred by {referringCustomer.name} {referringCustomer.last_name})
                      </span>
                    )}
                    {selectedCustomer.lead_source === "Other" && selectedCustomer.lead_source_description && (
                      <span className="ml-1 text-muted-foreground">
                        ({selectedCustomer.lead_source_description})
                      </span>
                    )}
                  </p>
                </div>
                {selectedCustomer.tags && selectedCustomer.tags.length > 0 && (
                  <div className="col-span-1 md:col-span-2">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedCustomer.tags.map((tag) => (
                        <Badge key={tag} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="estimates">
          <CustomerEstimates customerId={selectedCustomer.id} />
        </TabsContent>
        <TabsContent value="invoices">
          <CustomerInvoices customerId={selectedCustomer.id} />
        </TabsContent>
        <TabsContent value="credits">
          <CustomerCredits customerId={selectedCustomer.id} />
        </TabsContent>
        <TabsContent value="notes">
          <CustomerNotes customerId={selectedCustomer.id} />
        </TabsContent>
        <TabsContent value="projects">
          <CustomerProjects customerId={selectedCustomer.id} />
        </TabsContent>
        <TabsContent value="emails">
          <CustomerEmailHistory customerId={selectedCustomer.id} />
        </TabsContent>
        <TabsContent value="referrals">
          <div className="space-y-6">
            {/* Who referred this customer */}
            {selectedCustomer.lead_source === "Word of Mouth" && referringCustomer && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Users className="w-5 h-5 mr-2" /> 
                    Who Referred {selectedCustomer.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div 
                    className="p-3 border rounded-md hover:bg-accent cursor-pointer flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2"
                    onClick={() => {
                      if (selectedCustomer.referred_by_id) {
                        navigateToCustomer(selectedCustomer.referred_by_id);
                      }
                    }}
                  >
                    <span className="font-medium">{referringCustomer.name} {referringCustomer.last_name}</span>
                    <Button variant="ghost" size="sm" className="w-full sm:w-auto">
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Customers referred by this customer */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-base">
                  <Users className="w-5 h-5 mr-2 flex-shrink-0" /> 
                  <span className="truncate">Customers Referred by {selectedCustomer.name}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {referredCustomers.length > 0 ? (
                  <div className="space-y-2">
                    {referredCustomers.map((customer) => (
                      <div 
                        key={customer.id} 
                        className="p-3 border rounded-md hover:bg-accent cursor-pointer flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2"
                        onClick={() => {
                          navigateToCustomer(customer.id);
                        }}
                      >
                        <span className="font-medium">{customer.name} {customer.last_name}</span>
                        <Button variant="ghost" size="sm" className="w-full sm:w-auto">
                          View
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-20" />
                    <p>This customer hasn't referred anyone yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
